import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { handleServiceError } from '../helpers/app';
import useAuth from '../hooks/useAuth';
import { AgentRole } from '../interfaces/Agent';
import { ResponseObject } from '../interfaces/App';
import { getAgentRoles } from '../services/agentRolesService';

interface AgentRolesContextType {
  roles: AgentRole[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getRoleById: (roleId: string) => AgentRole | undefined;
}

export const AgentRolesContext = createContext<
  AgentRolesContextType | undefined
>(undefined);

interface AgentRolesProviderProps {
  children: ReactNode;
}

export const AgentRolesProvider: React.FC<AgentRolesProviderProps> = ({
  children,
}) => {
  const [roles, setRoles] = useState<AgentRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { currentUser } = useAuth();

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.id) {
        setRoles([]);
        setLoading(false);
        return;
      }

      const response: ResponseObject = await getAgentRoles(currentUser?.id);
      const rolesData = response?.message || [];
      setRoles(rolesData);
    } catch (err: unknown) {
      setError(err as Error);
      setRoles([]);
      handleServiceError(err, 'agent');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [currentUser?.id]);

  const getRoleById = (roleId: string): AgentRole | undefined => {
    return roles.find(role => role.id === roleId);
  };

  const value: AgentRolesContextType = {
    roles,
    loading,
    error,
    refetch: fetchRoles,
    getRoleById,
  };

  return (
    <AgentRolesContext.Provider value={value}>
      {children}
    </AgentRolesContext.Provider>
  );
};

export const useAgentRoles = (): AgentRolesContextType => {
  const context = useContext(AgentRolesContext);
  if (!context) {
    throw new Error('useAgentRoles must be used within an AgentRolesProvider');
  }
  return context;
};
